import type { SxProps, Theme } from '@mui/material';
import { useCallback, useState } from 'react';
import { SmartReportComponents } from '@/admins/SmartReports/components/Components';
import type { ComponentDefinition } from '@/admins/SmartReports/components/Components/types';
import type { EditSmartReportContextType } from '@/admins/SmartReports/components/EditSmartReport/EditSmartReportContext';
import { useEditSmartReport } from '@/admins/SmartReports/components/EditSmartReport/EditSmartReportContext';
import type { SlotsEnum } from '@/admins/SmartReports/components/Layouts/TitlePage/types';
import { getPlaceholderComponent } from '@/admins/SmartReports/helpers';
import { useGetPageComponent } from '@/admins/SmartReports/hooks/useGetPageComponent';
import { SmartReportComponentTypesEnum } from '@/admins/SmartReports/types';
import { DropContainer } from '@/components/DragAndDrop/DropContainer';

type SlotEditProps = {
  slotId: SlotsEnum;
  sx?: SxProps<Theme>;
  defaultData?: string | null;
  accept?: SmartReportComponentTypesEnum[];
};

export const SlotEdit = ({
  slotId,
  sx = {},
  accept = Object.values(SmartReportComponentTypesEnum),
}: SlotEditProps) => {
  const { portfolio, selectedPage, onComponentLoad, resetPageSlot } =
    useEditSmartReport() as unknown as EditSmartReportContextType;
  const { slotData, slotConfig } = useGetPageComponent(selectedPage, slotId);
  const [componentConfig, setComponentConfig] = useState(() => slotConfig || null);

  const handleDrop = (draggedComponent: ComponentDefinition<unknown>) => {
    resetPageSlot(slotId);
    setComponentConfig(SmartReportComponents.find(comp => comp.id === draggedComponent.id) || null);
  };

  const handleRemove = useCallback(
    e => {
      e.stopPropagation();
      // For some reason, the async/await here makes the state do not update properly, so keep it without it
      resetPageSlot(slotId);
      setComponentConfig(null);
    },
    [slotId, resetPageSlot],
  );

  if (!componentConfig) {
    // Render a placeholder component if no component is configured
    const PlaceholderComponent = getPlaceholderComponent(accept);
    return (
      <DropContainer accept={accept} onDrop={handleDrop}>
        <PlaceholderComponent sx={sx} slotId={slotId} />
      </DropContainer>
    );
  }

  const { Component } = componentConfig;

  return (
    <DropContainer accept={accept} onDrop={handleDrop} onRemove={handleRemove}>
      <Component
        sx={{ padding: 1, ...sx }}
        portfolio={portfolio}
        persistedData={slotData}
        onComponentLoad={({ componentId, data }) =>
          onComponentLoad({
            componentId,
            slotId,
            data,
          })
        }
      />
    </DropContainer>
  );
};
